import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import type {
  PlaneSize,
  PlaneTypes,
  Position2D,
} from '@Components/BigBrowse/Model/ModelTypes';
import { getWindowProps } from '@Utils/window';

import type { ProductImageDesign } from '../../common/MultiSectionView.types';
import { getProductImages } from './MerchandiserCommon';

export const MERCHANDISER_WIDTH = 1440;
const MERCHANDISER_HEIGHT = 971;
const MERCHANDISER_LANDSCAPE_ASPECT_RATIO =
  MERCHANDISER_WIDTH / MERCHANDISER_HEIGHT;

const CONTENT_WIDTH = 1229;
const CONTENT_HEIGHT = 807;
const CONTENT_ASPECT_RATIO = CONTENT_WIDTH / CONTENT_HEIGHT;

// These are derived from Figma
const productImageDesigns: ProductImageDesign[] = [
  {
    size: 280,
    left: 1059 - 111,
    top: 370 - 88,
    animatedOutDx: 391,
    animatedOutDy: 6,
    radius: 45,
  },
  {
    size: 260,
    left: 111 - 111,
    top: 257 - 88,
    animatedOutDx: -481,
    animatedOutDy: 169,
    radius: 45,
  },
  {
    size: 205,
    left: 1051 - 111,
    top: 88 - 88,
    animatedOutDx: 25,
    animatedOutDy: 25,
    radius: 45,
  },
  {
    size: 254,
    left: 746 - 111,
    top: 640 - 88,
    animatedOutDx: -25,
    animatedOutDy: -25,
    radius: 45,
  },
  {
    size: 117,
    left: 604 - 111,
    top: 698 - 88,
    animatedOutDx: -114,
    animatedOutDy: -127,
    radius: 30,
  },
];

export const calculateMerchandiserInternalsLandscape = (
  gridCard: GridCard<PlaneTypes.Layout_Merchandiser>
) => {
  const data = gridCard.data!;
  const boxSize = getBoxSizeLandscape(gridCard);
  const contentBoxSize = getContentBoxSize(boxSize);
  const centralImageSize = getCentralImageSize(contentBoxSize);
  const centralImagePosition = getCentralImagePosition({
    contentSize: contentBoxSize,
    imageSize: centralImageSize,
  });
  const exploreLabelPosition = getExploreLabelPosition(contentBoxSize);

  data.internals = {
    boxSize,
    contentBoxSize,
    contentPosition: { x: 0, y: 0 },
    centralImageSize,
    centralImagePosition,
    productImages: getProductImages(gridCard, contentBoxSize, {
      productImageDesigns,
      contentWidthDesign: CONTENT_WIDTH,
      contentHeightDesign: CONTENT_HEIGHT,
    }),
    exploreLabel: {
      fontSize: (35 * boxSize.width) / CONTENT_WIDTH,
      position: exploreLabelPosition,
      anchorY: 'bottom',
      color: data.dtoData?.fontColour,
      animateOutPosition: {
        x: exploreLabelPosition.x + (50 * contentBoxSize.width) / CONTENT_WIDTH,
        y:
          exploreLabelPosition.y +
          (100 * contentBoxSize.height) / CONTENT_HEIGHT,
      },
    },
  };
};

export const getHeightLandscape = (): number => {
  const { width, height } = getWindowProps();
  const aspectHeight = Math.floor(width / MERCHANDISER_LANDSCAPE_ASPECT_RATIO);

  return Math.min(aspectHeight, height); // makes sure that height does not exceed screen height
};

const getBoxSizeLandscape = (
  gridCard: GridCard<PlaneTypes.Layout_Merchandiser>
): PlaneSize => ({
  width: getWindowProps().width,
  height: gridCard.size.height,
});

const getContentBoxSize = (boxSize: PlaneSize): PlaneSize => {
  const height = Math.round(
    (boxSize.height * CONTENT_HEIGHT) / MERCHANDISER_HEIGHT
  );

  const width = height * CONTENT_ASPECT_RATIO;
  return { width, height };
};

const getCentralImageSize = (contentBoxSize: PlaneSize): PlaneSize => ({
  width: Math.floor((860 / CONTENT_WIDTH) * contentBoxSize.width),
  height: Math.floor((537 / CONTENT_HEIGHT) * contentBoxSize.height),
});

const getCentralImagePosition = ({
  contentSize,
  imageSize,
}: {
  contentSize: PlaneSize;
  imageSize: PlaneSize;
}): Position2D => {
  const left = Math.floor((163 / CONTENT_WIDTH) * contentSize.width);
  const top = Math.floor((109 / CONTENT_HEIGHT) * contentSize.height);

  const x = Math.floor(imageSize.width / 2 + left - contentSize.width / 2);
  const y = -Math.floor(imageSize.height / 2 + top - contentSize.height / 2);

  return { x, y };
};

const getExploreLabelPosition = (boxSize: PlaneSize): Position2D => ({
  x: -boxSize.width / 2 - (52 / CONTENT_WIDTH) * boxSize.width,
  y: -boxSize.height / 2 + (11 / CONTENT_HEIGHT) * boxSize.height,
});
